import type { FC, ReactNode } from 'react'
import type { MediaItem } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'

type MaskDirection = 'up' | 'down' | 'left' | 'right' | 'center'

interface MediaFrameProps {
    image: MediaItem
    sizes: string
    className?: string
    imageClassName?: string
    /** Wipe the frame open when it scrolls into view. */
    mask?: MaskDirection
    /** Parallax strength in percent of the frame. 0 turns it off. */
    parallax?: number
    preload?: boolean
    children?: ReactNode
}

/**
 * A photograph inside a clipped frame, with optional scroll parallax and
 * mask reveal. The motion is wired up by useScrollMotion on the page.
 */
export const MediaFrame: FC<MediaFrameProps> = ({
    image,
    sizes,
    className,
    imageClassName,
    mask,
    parallax = 0,
    preload = false,
    children
}): ReactNode => {
    return (
        <div data-mask={mask} className={cn('relative overflow-hidden bg-sand', className)}>
            <div
                data-parallax={parallax > 0 ? parallax : undefined}
                className={cn('absolute inset-x-0', parallax > 0 ? '-top-[14%] -bottom-[14%]' : 'inset-y-0')}
            >
                <div data-mask-inner={mask ? '' : undefined} className='relative h-full w-full'>
                    <Image
                        src={image.src}
                        alt={image.alt}
                        fill
                        sizes={sizes}
                        preload={preload}
                        className={cn('object-cover', imageClassName)}
                        style={{ objectPosition: image.position }}
                    />
                </div>
            </div>

            {children}
        </div>
    )
}
